import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'
import type { StatusTone } from '@/lib/status'

/**
 * A tinted label for a value that is not a status.
 *
 * Anything that describes *state* — a job, a judgment, a capture's
 * completeness — goes through `Status` and its registry instead, so those can
 * never disagree between screens. This is for the rest: a format tag, a
 * "required" marker, a count.
 */
export type BadgeTone = StatusTone

const TONES: Record<BadgeTone, string> = {
  neutral: 'border-line bg-surface-sunken text-ink-secondary',
  accent: 'border-accent-ring/45 bg-accent-soft text-accent-text',
  good: 'border-good/30 bg-good-soft text-good',
  warning: 'border-warning/30 bg-warning-soft text-warning',
  serious: 'border-serious/30 bg-serious-soft text-serious',
  critical: 'border-critical/30 bg-critical-soft text-critical',
}

export interface BadgeProps {
  tone?: BadgeTone
  children: ReactNode
  className?: string
  icon?: ReactNode
  /** Native tooltip, for detail that would be noise in the badge itself. */
  title?: string
}

export function Badge({ tone = 'neutral', children, className, icon, title }: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5',
        'text-2xs font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  )
}

/**
 * A count that rides alongside a label — the filter count on a button, the
 * unread number on a tab.
 */
export function CountBadge({
  value,
  active,
  className,
}: {
  value: number
  active?: boolean
  className?: string
}) {
  return (
    <span
      className={cn(
        'tabular inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full px-1.5 text-2xs font-medium',
        active
          ? 'bg-accent text-accent-contrast'
          : 'bg-surface-active text-ink-secondary',
        className,
      )}
    >
      {value.toLocaleString()}
    </span>
  )
}
